import { ButtonBase, MenuItem, Select, Tooltip } from '@material-ui/core';
import classNames from 'classnames';
import { AddButton } from '~/common/add-button';
import { PageTitle } from '~/features/dashboard/components/page-title';
import { selectCalendarEvents } from '~/features/dashboard/store/selectors';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Calendar, { CalendarProps, CalendarTileProperties } from 'react-calendar';
import { useSelector } from 'react-redux';
import { CALENDAR_EVENT_DATE_FORMAT, CALENDAR_PAGE_TILE_FORMAT } from '~/config/formatters';
import * as variables from '~/styles/variables.scss';
import { useHeaderColorChanger } from '~/utils/hooks/useHeaderColorChanger';
import css from './styles.module.scss';

interface CalendarPageProps {}

interface TopBarProps {
    date: Date;
    setDate: (param: Date) => any;
    months: string[];
    currentMonth: string;
}

const TopBar: React.FC<TopBarProps> = ({ date, setDate, months, currentMonth }) => {
    const changeMonth = useCallback(
        (count: number, _date: Date) => {
            setDate(moment(_date).add(count, 'month').toDate());
        },
        [setDate]
    );

    const makeChangeMonthHandler = useCallback((count: number) => () => changeMonth(count, date), [date, changeMonth]);

    const month = useMemo(() => moment(date).format('MMMM'), [date]);

    const year = useMemo(() => moment(date).format('YYYY'), [date]);

    const nextMonth = useMemo(() => makeChangeMonthHandler(1), [makeChangeMonthHandler]);

    const prevMonth = useMemo(() => makeChangeMonthHandler(-1), [makeChangeMonthHandler]);

    return (
        <div className={classNames(css.topBar, 'topBar')}>
            <PageTitle className={'cell'}>Calendar</PageTitle>
            <div className={'cell month'}>
                <ButtonBase onClick={prevMonth} className={'arrow prev'}>
                    {'<'}
                </ButtonBase>
                <div className={'monthName'}>
                    {month} {year}
                </div>
                <ButtonBase onClick={nextMonth} className={'arrow next'}>
                    {'>'}
                </ButtonBase>
            </div>
            <div className={'cell monthSelect'}>
                <Select disabled variant={'filled'} value={currentMonth} displayEmpty multiple={false} native={false} disableUnderline>
                    <MenuItem value={''} disabled>
                        Month
                    </MenuItem>
                    {months.map(month => (
                        <MenuItem key={month} value={month}>
                            {month}
                        </MenuItem>
                    ))}
                </Select>
            </div>
        </div>
    );
};

export const CalendarPage: React.FC<CalendarPageProps> = () => {
    const [date, setDate] = useState<Date>(new Date());

    useHeaderColorChanger('white', variables.colorGrayPrimaryText);

    const events = useSelector(selectCalendarEvents);

    const Tile: React.FC<CalendarTileProperties> = useCallback(
        ({ date }) => {
            const formattedDate = moment(date).format(CALENDAR_EVENT_DATE_FORMAT);
            return (
                <Tooltip title="Retention End Date" placement="top" disableHoverListener={!events.includes(formattedDate)}>
                    <div className={classNames(css.calendarTile, 'calendarTile')}>
                        <div className={'day'}>
                            <span className={'text'}>{moment(date).format(CALENDAR_PAGE_TILE_FORMAT)}</span>
                        </div>
                    </div>
                </Tooltip>
            );
        },
        [events]
    );

    const getTileClassName: CalendarProps['tileClassName'] = useCallback(
        ({ date }) => {
            const formattedDate = moment(date).format(CALENDAR_EVENT_DATE_FORMAT);

            return events.includes(formattedDate) ? 'with-event' : '';
        },
        [events]
    );

    const [selectedMonth] = useState('');

    useEffect(() => {
        if (!selectedMonth) {
            return;
        }

        const year = moment(date).year();
        const newMonth = moment().month(selectedMonth).year(year);

        setDate(newMonth.toDate());
    }, [selectedMonth, date]);

    const months = useMemo(() => moment.months(), []);

    const onDateChange = useCallback((newDate: Date | Date[]) => {
        setDate(Array.isArray(newDate) ? newDate[0] : newDate);
    }, []);

    return (
        <div className={css.module}>
            <TopBar date={date} setDate={setDate} currentMonth={selectedMonth} months={months} />
            <div className={css.subTopBar}>
                <AddButton className={css.addEventBtn}>Create</AddButton>
            </div>
            <Calendar
                showNavigation={false}
                minDetail={'month'}
                maxDetail={'month'}
                view={'month'}
                value={date}
                onChange={onDateChange}
                nextLabel={''}
                prevLabel={''}
                next2Label={null}
                prev2Label={null}
                tileContent={Tile}
                tileClassName={getTileClassName}
            />
        </div>
    );
};
